import React, { useEffect, useMemo, useRef, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../../components/ErrorBoundary';
import { useProjects } from '../../../context/ProjectsContext';
import ProjectFilterSelect from '../../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../../components/ResetFiltersButton';
import TableEmptyRow from '../../../components/TableEmptyRow';
import StatusBadge from '../../../components/StatusBadge';
import { bankGuaranteesAPI } from '../../../services/authService';
import { banksAPI, cashMarginAPI } from '../../../services/bgFinanceService';
import { DEPOSIT_TYPE_OPTIONS, emptyDepositForm, formatCurrency } from './bgShared';
import BgExportButtons from './BgExportButtons';
import { downloadBgTableCsv, exportBgTablePdf } from './bgExportUtils';
import { notifyError, notifySuccess, notifyWarning } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';
import { formatDateIN } from '../../../utils/dateFormat';

const mapDeposit = (row) => ({
  id: row.id,
  bank_id: row.bank_id,
  bank_name: row.bank?.bank_name || '—',
  project_id: row.project_id,
  project_name: row.project?.name || '—',
  bank_guarantee_id: row.bank_guarantee_id,
  bg_no: row.bank_guarantee?.bg_no || '—',
  deposit_type: row.deposit_type || 'CASH_MARGIN',
  deposit_amount: row.deposit_amount ?? 0,
  released_amount: row.released_amount ?? 0,
  balance_cash_blocked: row.balance_cash_blocked ?? (row.deposit_amount - row.released_amount),
  deposit_date: row.deposit_date || '',
  reference_no: row.reference_no || '',
  status: row.status || 'ACTIVE',
  remarks: row.remarks || '',
  lien_marked: !!row.lien_marked,
  refundable: row.refundable !== false,
});

const CashMarginRegister = () => {
  const { projects: projectOptions } = useProjects();
  const projects = safeArray(projectOptions);
  const [deposits, setDeposits] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bgs, setBgs] = useState([]);
  const [projectFilter, setProjectFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [releaseAmount, setReleaseAmount] = useState('');
  const [form, setForm] = useState(emptyDepositForm);
  const tableRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const loadDeposits = async () => {
    try {
      const params = {};
      if (projectFilter !== 'all') params.projectId = projectFilter;
      const resp = await cashMarginAPI.getAll(params);
      setDeposits(safeArray(resp?.data?.data).map(mapDeposit));
    } catch (err) {
      console.error('Load deposits failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to load cash margin deposits.');
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    banksAPI.getAll().then((r) => setBanks(safeArray(r?.data?.data))).catch(() => setBanks([]));
    bankGuaranteesAPI.getAll({ page: 1, pageSize: 500 }).then((r) => {
      const { rows } = unwrapListResponse(r);
      setBgs(safeArray(rows));
    }).catch(() => setBgs([]));
  }, []);

  useEffect(() => { setLoading(true); loadDeposits(); }, [projectFilter]);

  const filteredDeposits = useMemo(() => deposits, [deposits]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyDepositForm(),
      project_id: projectFilter !== 'all' ? String(projectFilter) : '',
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      bank_id: String(row.bank_id || ''),
      project_id: row.project_id ? String(row.project_id) : '',
      bank_guarantee_id: row.bank_guarantee_id ? String(row.bank_guarantee_id) : '',
      deposit_type: row.deposit_type || 'CASH_MARGIN',
      deposit_amount: row.deposit_amount != null ? String(row.deposit_amount) : '',
      deposit_date: row.deposit_date || new Date().toISOString().slice(0, 10),
      reference_no: row.reference_no || '',
      lien_marked: !!row.lien_marked,
      refundable: row.refundable !== false,
      remarks: row.remarks || '',
    });
    setShowModal(true);
  };

  const openRelease = (row) => {
    setReleasing(row);
    const balance = (row.deposit_amount || 0) - (row.released_amount || 0);
    setReleaseAmount(String(balance));
    setShowReleaseModal(true);
  };

  const buildPayload = () => ({
    bank_id: Number(form.bank_id),
    project_id: form.project_id ? Number(form.project_id) : null,
    bank_guarantee_id: form.bank_guarantee_id ? Number(form.bank_guarantee_id) : null,
    deposit_type: form.deposit_type,
    deposit_amount: Number(form.deposit_amount || 0),
    deposit_date: form.deposit_date || null,
    reference_no: form.reference_no || null,
    lien_marked: !!form.lien_marked,
    refundable: !!form.refundable,
    remarks: form.remarks || null,
  });

  const handleSave = async () => {
    if (!form.bank_id || !form.deposit_amount) {
      notifyWarning('Bank and deposit amount are required.');
      return;
    }
    if (!(await confirmDialog(editing ? 'Save deposit changes?' : 'Record this cash margin deposit?', editing ? 'Update Deposit' : 'New Deposit'))) return;

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        const resp = await cashMarginAPI.update(editing.id, payload);
        const mapped = mapDeposit(resp?.data);
        setDeposits((prev) => prev.map((d) => (d.id === editing.id ? mapped : d)));
        notifySuccess('Deposit updated.');
      } else {
        const resp = await cashMarginAPI.create(payload);
        const mapped = mapDeposit(resp?.data);
        setDeposits((prev) => [mapped, ...prev]);
        notifySuccess('Deposit recorded.');
      }
      setShowModal(false);
    } catch (err) {
      console.error('Save deposit failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to save deposit.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async () => {
    if (!releasing?.id) return;
    const amt = Number(releaseAmount || 0);
    if (amt <= 0) {
      notifyWarning('Release amount must be greater than zero.');
      return;
    }
    if (!(await confirmDialog(`Release ${formatCurrency(amt)} from this deposit?`, 'Release Cash Margin'))) return;

    setSubmitting(true);
    try {
      const resp = await cashMarginAPI.release(releasing.id, { release_amount: amt });
      const mapped = mapDeposit(resp?.data);
      setDeposits((prev) => prev.map((d) => (d.id === releasing.id ? mapped : d)));
      setShowReleaseModal(false);
      notifySuccess('Cash margin released.');
    } catch (err) {
      console.error('Release failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to release cash margin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!(await confirmDialog(`Deactivate deposit ${row.reference_no || row.id}?`, 'Deactivate Deposit'))) return;
    try {
      await cashMarginAPI.delete(row.id);
      setDeposits((prev) => prev.filter((d) => d.id !== row.id));
      notifySuccess('Deposit deactivated.');
    } catch (err) {
      console.error('Delete deposit failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to deactivate deposit.');
    }
  };

  const getStatusTone = (status) => {
    if (status === 'RELEASED' || status === 'CLOSED') return 'closed';
    if (status === 'PARTIALLY_RELEASED') return 'under-review';
    return 'active';
  };

  if (loading && deposits.length === 0) {
    return <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading cash margin register...</p>;
  }


  const handleExportPdf = () => exportBgTablePdf({ title: 'Cash Margin Register', subtitle: '', element: tableRef.current });
  const handleExportCsv = () => downloadBgTableCsv({ element: tableRef.current, filename: 'cash-margin-register.csv' });

  return (
    <div>
      <div className="section-header">
        <h2>Cash Margin Register <span className="count-badge">{filteredDeposits.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}><BgExportButtons onDownloadPdf={handleExportPdf} onDownloadCsv={handleExportCsv} /><button type="button" className="btn-primary" onClick={openCreate}>Add Deposit</button></div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect value={projectFilter} onChange={setProjectFilter} projects={projects} />
        <ResetFiltersButton onClick={() => setProjectFilter('all')} />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Bank</th>
              <th>Project</th>
              <th>BG/Bond No.</th>
              <th>Deposit Type</th>
              <th className="amount-cell">Deposit Amount</th>
              <th className="amount-cell">Released Amount</th>
              <th className="amount-cell">Balance Cash Blocked</th>
              <th>Deposit Date</th>
              <th>Reference No.</th>
              <th>Status</th>
              <th className="bg-export-exclude" style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeposits.length === 0 ? (
              <TableEmptyRow colSpan={11} message="No cash margin deposits found." />
            ) : (
              filteredDeposits.map((row) => (
                <tr key={row.id}>
                  <td>{row.bank_name}</td>
                  <td>{row.project_name}</td>
                  <td>{row.bg_no}</td>
                  <td>{DEPOSIT_TYPE_OPTIONS.find((o) => o.value === row.deposit_type)?.label || row.deposit_type}</td>
                  <td className="amount-cell">{formatCurrency(row.deposit_amount)}</td>
                  <td className="amount-cell">{formatCurrency(row.released_amount)}</td>
                  <td className="amount-cell">{formatCurrency(row.balance_cash_blocked)}</td>
                  <td>{formatDateIN(row.deposit_date)}</td>
                  <td>{row.reference_no || '—'}</td>
                  <td><StatusBadge tone={getStatusTone(row.status)}>{row.status}</StatusBadge></td>
                  <td className="bg-export-exclude" style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => openEdit(row)}>Edit</button>
                    {' '}
                    {row.status !== 'RELEASED' && row.status !== 'CLOSED' && (
                      <button type="button" className="btn-secondary btn-sm" onClick={() => openRelease(row)}>Release</button>
                    )}
                    {' '}
                    <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => handleDelete(row)}>Deactivate</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editing ? 'Edit Cash Margin Deposit' : 'Add Cash Margin Deposit'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Bank <span className="req">*</span></label>
                  <select value={form.bank_id} onChange={(e) => setForm((p) => ({ ...p, bank_id: e.target.value }))}>
                    <option value="">Select Bank</option>
                    {banks.filter((b) => b.is_active !== false).map((b) => (
                      <option key={b.id} value={b.id}>{b.bank_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Project</label>
                  <select value={form.project_id} onChange={(e) => setForm((p) => ({ ...p, project_id: e.target.value }))}>
                    <option value="">Select Project</option>
                    {projects.filter((p) => p.status === 'Active').map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Link BG/Bond</label>
                  <select value={form.bank_guarantee_id} onChange={(e) => setForm((p) => ({ ...p, bank_guarantee_id: e.target.value }))}>
                    <option value="">None</option>
                    {bgs.map((bg) => (
                      <option key={bg.id} value={bg.id}>{bg.bgNo || bg.bg_no || `BG-${bg.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Deposit Type</label>
                  <select value={form.deposit_type} onChange={(e) => setForm((p) => ({ ...p, deposit_type: e.target.value }))}>
                    {DEPOSIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Deposit Amount <span className="req">*</span></label>
                  <input type="number" value={form.deposit_amount} onChange={(e) => setForm((p) => ({ ...p, deposit_amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Deposit Date</label>
                  <input type="date" value={form.deposit_date} onChange={(e) => setForm((p) => ({ ...p, deposit_date: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Reference No.</label>
                <input type="text" value={form.reference_no} onChange={(e) => setForm((p) => ({ ...p, reference_no: e.target.value }))} />
              </div>
              <div className="form-row">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.lien_marked} onChange={(e) => setForm((p) => ({ ...p, lien_marked: e.target.checked }))} />
                  Lien Marked
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.refundable} onChange={(e) => setForm((p) => ({ ...p, refundable: e.target.checked }))} />
                  Refundable
                </label>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea rows={2} value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                <button type="button" className="btn-primary" onClick={handleSave} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReleaseModal && releasing && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Release Cash Margin</h2>
              <button type="button" className="btn-close" onClick={() => setShowReleaseModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                Balance available: {formatCurrency((releasing.deposit_amount || 0) - (releasing.released_amount || 0))}
              </p>
              <div className="form-group">
                <label>Release Amount <span className="req">*</span></label>
                <input type="number" value={releaseAmount} onChange={(e) => setReleaseAmount(e.target.value)} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowReleaseModal(false)} disabled={submitting}>Cancel</button>
                <button type="button" className="btn-primary" onClick={handleRelease} disabled={submitting}>{submitting ? 'Releasing...' : 'Release'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashMarginRegister;
